// src/components/Markdown.tsx
"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "@/i18n/navigation";
import { hashtagToHref } from "@/lib/hashtag";

type MarkdownProps = {
  content: string;
  className?: string;
  /** Path without locale prefix. next-intl Link adds the locale. */
  tagHrefPrefix?: string;
};

function linkify(line: string, tagHrefPrefix: string): string {
  // Skip lines that already contain markdown link/image syntax or code
  if (/!?\[[^\]]*\]\([^)]*\)/.test(line)) return line;
  if (/`/.test(line)) return line;

  let out = line;

  // Hashtags → markdown links with a real /timeline?tag=... href
  out = out.replace(
    /(^|\s)(#[\p{L}\p{N}_\u200c]+)/gu,
    (_m, lead, tag) => `${lead}[${tag}](${hashtagToHref(tag, tagHrefPrefix)})`,
  );

  // Bare URLs → external markdown links
  out = out.replace(
    /(?<!\]\()\b((?:https?:\/\/)?(?:[\w-]+\.)+[a-z]{2,}(?:\/[^\s<>()]*)?)/gi,
    (url, _g1, offset: number, full: string) => {
      const before = full.slice(0, offset);
      if (/\[[^\]]*$/.test(before)) return url;
      if (/\]\([^)]*$/.test(before)) return url;
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return `[${url}](${href})`;
    },
  );

  return out;
}

export default function Markdown({
  content,
  className,
  tagHrefPrefix = "/timeline",
}: MarkdownProps) {
  if (!content) return null;

  const lines = content.split(/\r?\n/);

  // Anything with ?tag= is our internal hashtag link
  const isTagHref = (href: string) =>
    href.includes("?tag=") || href.includes("&tag=");

  return (
    <div className={className ?? "markdown-body"}>
      {lines.map((line, i) =>
        line.trim() === "" ? (
          <br key={i} />
        ) : (
          <div
            key={i}
            dir="auto"
            style={{ margin: 0, unicodeBidi: "plaintext" }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <>{props.children}</>,

                a: ({ node, href, children, ...props }) => {
                  const h = typeof href === "string" ? href : "";

                  // Internal hashtag link → next-intl Link (locale-aware)
                  if (isTagHref(h)) {
                    // h is like "/timeline?tag=%D8%A2%D9%88%D8%A7%D8%B2"
                    const [path, query] = h.split("?");
                    return (
                      <Link href={`${path}?${query}`} {...props}>
                        {children}
                      </Link>
                    );
                  }

                  const isExternal = /^https?:\/\//i.test(h);
                  return (
                    <a
                      href={h}
                      {...(isExternal
                        ? {
                            target: "_blank",
                            rel: "noopener noreferrer nofollow",
                          }
                        : {})}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {linkify(line, tagHrefPrefix)}
            </ReactMarkdown>
          </div>
        ),
      )}
    </div>
  );
}
